/**
 * 需求 R94 — 前端「勾選後產生的輸出清單」統一排序比較器。
 *
 * 排序基準完全沿用 需求 R80（PM 拍板 D1–D7 + D2 修正），
 * 與後端 src/server/utils/productSort.js 一一對應：
 *   1. 排序鍵＝產品編號 productCode（非 systemCode）
 *   2. productCode 字串長度 >= 7 者為主排序群，依 productCode 升冪
 *      （新碼等寬零補齊，字典序＝數字序，A000009 在 A000010 之前）
 *   3. 字串長度 < 7 者（含空編號）整批殿後，群內同樣 productCode 升冪
 *   4. 同 productCode 再依 createdAt 新→舊
 *
 * 為何要「前端再寫一份」而不共用後端那份：
 *   CRA（react-scripts）的 ModuleScopePlugin 禁止 import src/client/src/ 以外的檔案，
 *   無法直接 require('../../../server/utils/productSort')。故此處為等價實作，
 *   並以 tests/clientProductSort.fixture.js 對同一組樣本斷言「前後端輸出順序完全相同」，
 *   防止日後只改一邊（例如門檻 7 碼）而漂移。
 *
 * PM 2026-07-31 逐項拍板：
 *   Q1 範圍：「產品管理」這裡不動（列表／匯出成本表本來就已走後端 productSort）。
 *   Q2 手動排序：未來沒有手動排序需求 → 自動重排不會覆蓋使用者的手動順序。
 *      ↑ 此前提在 需求 R114（報價單品項手動排序）被重新檢視過，結論是「維持不變」：
 *        PM 2026-08-18 留言逐字「理解，這部分排序的確會與原本的設定衝突，
 *        本案不執行，人工丟到已取消」→ 需求 R114 結案為不執行，本檔 Q2 前提續成立，
 *        sortQuoteItems 及其兩個呼叫點（QuoteCreatePage / QuoteEditPage 的 handleAddProducts）
 *        一律不得改成「新品接尾端 / 加手動排序旗標」。若日後要做手動排序，須由 PM 另開新票重新拍板。
 *        規格與衝突分析保留在 需求 R114 任務描述（Q1-Q7），本 repo 不留半成品程式碼。
 *   Q3 重排時機：在「按下添加到報價單」當下重排一次（不是每次 render 都強制排序，
 *      使用者中途修改欄位不會讓列順序跳動）。
 *   Q4 存量資料：現行（既有）報價單不理 → 開啟／匯出既有報價單「不」自動重排；
 *      只有當使用者按下「添加到報價單」時，才把整份品項一併重排（Q3 的必然結果）。
 *   Q5 tiebreak 退化：PM 第一次回「請說明此問題，不理解」，經白話說明後於同日回覆逐字
 *      「維持這兩筆原本的前後順序」→ 即本檔 Q5 規則，見下方。
 *   Q6 型錄排版：沒有人用點選順序控制排版 → 型錄一律依本排序邏輯輸出。
 *
 * Q5 規則（PM 2026-07-31 拍板「維持這兩筆原本的前後順序」）—— 問題與作法：
 *   需求 R60 之後產品的唯一鍵是 systemCode，productCode（貨編）允許重複。
 *   規則第 4 條說「同 productCode 時，比較誰的『建立時間 createdAt』較新排前面」，
 *   但編輯既有報價單時，畫面上的品項是報價單自己存下來的快照，快照裡沒有「產品建立時間」；
 *   要拿到 createdAt 得再打一次產品 API。
 *   → 依 PM 拍板：拿不到建立時間的品項「維持原本的前後順序」，不另外多打 API 補撈。
 *
 *   實作上「維持原本的前後順序」不是靠 Array.prototype.sort 的穩定性就夠：
 *   缺 createdAt 時比較器回 0，會讓比較器變成「不一致（intransitive）比較器」
 *   （A 缺時間 → A=B、A=C，但 B、C 之間卻分得出先後），此時 ECMAScript 規格明定
 *   排序結果為 implementation-defined，穩定性不再受保障（V8 實測目前會保序，但不可倚賴）。
 *   → 故本檔的清單排序改為「分群 + 定位」而非單純 sort：
 *       a. 先以 (是否滿 7 碼, productCode) 分群並排序群組（一致比較器，群內維持原始順序）
 *       b. 群內：缺 createdAt 的品項「釘」在原本的位置不動；
 *          有 createdAt 的品項在剩下的位置上依新→舊填回（時間相同者維持原始先後）
 *     如此無論 JS 引擎如何實作 sort，缺建立時間的品項一定維持原本的前後順序，
 *     且同一份資料重複排序結果不變（冪等）。
 *   影響面極小：只有「兩筆產品的貨編一模一樣、且缺建立時間」時才會走到此規則。
 */

// D2（需求 R80 2026-07-22 修正）：滿 7 碼 = 字串長度 >= 7；長度 < 7（含空字串）殿後。
// 與 src/server/utils/productSort.js 的 FULL_CODE_MIN_LENGTH 必須一致。
export const FULL_CODE_MIN_LENGTH = 7;

/** 判定 productCode 是否「滿 7 碼」（字串長度 >= 7）。null/undefined/'' 皆視為不滿。 */
export function isFullFormatCode(code) {
  const s = code == null ? '' : String(code);
  return s.length >= FULL_CODE_MIN_LENGTH;
}

/** createdAt → 毫秒；缺值或無法解析回傳 null（見檔頭 Q5）。 */
function toTime(value) {
  if (value == null || value === '') return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/** productCode → 字串（null/undefined 視為空字串）。 */
function toCode(value) {
  return value == null ? '' : String(value);
}

/**
 * 產品比較器（兩兩比較用），等效於後端 compareProductsForList：
 *   1. 滿 7 碼者排在不滿 7 碼者之前
 *   2. 同群內 productCode 升冪
 *   3. 同 productCode 再依 createdAt 新→舊；任一方缺 createdAt → 回 0（無法比較，Q5）
 *
 * 注意：回 0 的情形會讓本比較器對「一筆缺時間、其餘有時間」的同碼群失去一致性，
 * 故清單排序請一律用下方 sortProductsForList / sortQuoteItems（已用分群+定位處理 Q5），
 * 不要直接把本比較器丟給 Array.prototype.sort。
 */
export function compareProductsForList(a, b) {
  const aShort = isFullFormatCode(a && a.productCode) ? 0 : 1;
  const bShort = isFullFormatCode(b && b.productCode) ? 0 : 1;
  if (aShort !== bShort) return aShort - bShort;

  const ac = toCode(a && a.productCode);
  const bc = toCode(b && b.productCode);
  if (ac !== bc) return ac < bc ? -1 : 1;

  const at = toTime(a && a.createdAt);
  const bt = toTime(b && b.createdAt);
  if (at == null || bt == null) return 0;
  return bt - at;
}

/**
 * 共用排序核心（Q5 規則的唯一實作處）。
 * @param {Array} list 待排序清單（不會被改動）
 * @param {Function} keyOf 取出 { productCode, createdAt } 排序鍵
 */
function sortByProductRule(list, keyOf) {
  const rows = (list || []).map((item, index) => {
    const key = keyOf(item) || {};
    return {
      item,
      index,
      short: isFullFormatCode(key.productCode) ? 0 : 1,
      code: toCode(key.productCode),
      time: toTime(key.createdAt),
    };
  });

  // (a) 只用「滿 7 碼旗標 + productCode」排群（一致比較器；同群回 0，
  // 由 ES2019 起保證的穩定排序維持群內原始順序）。
  rows.sort((a, b) => {
    if (a.short !== b.short) return a.short - b.short;
    if (a.code !== b.code) return a.code < b.code ? -1 : 1;
    return 0;
  });

  // (b) 逐群處理 createdAt：缺時間者釘在原位，有時間者在剩下的位置依新→舊填回。
  let start = 0;
  while (start < rows.length) {
    let end = start + 1;
    while (end < rows.length && rows[end].short === rows[start].short && rows[end].code === rows[start].code) {
      end += 1;
    }
    const slots = [];
    const timed = [];
    for (let i = start; i < end; i += 1) {
      if (rows[i].time != null) {
        slots.push(i);
        timed.push(rows[i]);
      }
    }
    if (timed.length > 1) {
      // 新→舊；時間相同者依原始索引維持先後（不依賴引擎穩定性）。
      timed.sort((a, b) => (b.time - a.time) || (a.index - b.index));
      slots.forEach((slot, i) => {
        rows[slot] = timed[i];
      });
    }
    start = end;
  }

  return rows.map((r) => r.item);
}

/**
 * 排序產品清單（不改動傳入陣列）。
 * 缺 createdAt 的品項維持原本的前後順序（Q5，PM 2026-07-31 拍板）。
 */
export function sortProductsForList(list) {
  return sortByProductRule(list, (p) => p || {});
}

/**
 * 報價單品項 → 排序用欄位。
 * 品項本身帶 productCode 快照；createdAt 則來自完整產品資料 _productData
 * （新增頁為勾選當下的搜尋結果、編輯頁為載入後補抓的 GET /api/products/:id）。
 * 兩者都拿不到時回 null，落入 Q5 的「維持原本前後順序」規則。
 */
function quoteItemSortKey(item) {
  return {
    productCode: (item && item.productCode) || '',
    createdAt: (item && item._productData && item._productData.createdAt) || (item && item.createdAt) || null,
  };
}

/** 報價單品項比較器（同一套基準，只是欄位取法不同）。 */
export function compareQuoteItems(a, b) {
  return compareProductsForList(quoteItemSortKey(a), quoteItemSortKey(b));
}

/** 排序報價單品項清單（不改動傳入陣列）。 */
export function sortQuoteItems(items) {
  return sortByProductRule(items, quoteItemSortKey);
}
